import { useAppConfigs } from '@config/config.hooks';
import { useIsLoggedIn } from '@gcl/react-core';
import { useGetProjectByProjectNoAsync } from '@project/project.queries';
import { routes } from '@routes/routes.models';
import { useGetMyDataAsync } from '@user/user.queries';
import * as React from 'react';
import { useNavigate } from '@routes/routes.hooks';
import { hasPermissionInArray } from '@userProfile/permission/permission.services';
import { ERolePermission } from '@userProfile/permission/permission.constants';
import { ApiErrorDisplay } from '@core/ApiErrorDisplay';
import { LOCAL_STORAGE_KEYS } from '@common/constants';
import { IProject } from '@project/project.models';

export interface IMyDataFetcherProps {}

export const MyDataFetcher: React.FC<IMyDataFetcherProps> = ({}) => {
  const appConfigs = useAppConfigs();
  const isLoggedIn = useIsLoggedIn();
  const { navigate } = useNavigate();
  const {
    data: project,
    isLoading: isLoadingProject,
    error: errorProject,
  } = useGetProjectByProjectNoAsync(appConfigs?.configs.projectNoFromUrl);
  const {
    data: myData,
    isLoading: isLoadingMyData,
    error: errorMyData,
  } = useGetMyDataAsync(
    { projectId: project?.item?.id },
    isLoggedIn &&
      (appConfigs?.configs.projectNoFromUrl === undefined ||
        !!(appConfigs?.configs.projectNoFromUrl && appConfigs?.configs.project?.projectNo)),
  );

  React.useEffect(() => {
    if (!isLoggedIn) return;

    if (!isLoadingMyData && myData?.item?.userProfile.isLocked) {
      navigate(routes.common.accountLocked.path, undefined, true, { replace: true });
      return;
    }

    if (!isLoadingProject && !project?.item && !!appConfigs?.configs.projectNoFromUrl) {
      navigate(routes.common.pageNotFound.path, undefined, false, { replace: true });
      return;
    }

    if (
      !isLoadingProject &&
      !isLoadingMyData &&
      !!appConfigs?.configs.projectNoFromUrl &&
      project?.item &&
      myData?.item?.projectUserProfile == null &&
      hasPermissionInArray(myData?.item?.permissions ?? [], ERolePermission.VIEWER) === false
    ) {
      navigate(routes.common.noProjectAccessRight.path, undefined, true, { replace: true });
      return;
    }

    if (!appConfigs.updateConfigs || typeof appConfigs.updateConfigs !== 'function') return;

    if (appConfigs.updateConfigs && typeof appConfigs.updateConfigs === 'function') {
      appConfigs.updateConfigs((configs) => {
        return {
          ...configs,
          myProjectUserProfile: myData?.item?.projectUserProfile,
          myUserProfile: myData?.item?.userProfile,
          myPermissions: myData?.item?.permissions,
          myCompanies: myData?.item?.belongsToCompanies,
          myTeams: myData?.item?.belongsToTeams,
          project: project?.item,
        };
      });
      setRecentProjects();
    }
  }, [JSON.stringify(myData), JSON.stringify(project), isLoggedIn]);

  const setRecentProjects = React.useCallback(() => {
    if (!project?.item) return;

    try {
      const recentProjectStr = localStorage.getItem(LOCAL_STORAGE_KEYS.RECENT_PROJECTS);
      if (!recentProjectStr || recentProjectStr.length <= 0) {
        localStorage.setItem(LOCAL_STORAGE_KEYS.RECENT_PROJECTS, JSON.stringify([project?.item]));
        return;
      }
      const recentProjects = (JSON.parse(recentProjectStr) ?? []) as IProject[];
      const newRecentProjects = [project?.item, ...recentProjects.filter((p) => p.id !== project?.item?.id)].slice(
        0,
        3,
      );
      localStorage.setItem(LOCAL_STORAGE_KEYS.RECENT_PROJECTS, JSON.stringify(newRecentProjects));
    } catch (error) {
      localStorage.setItem(LOCAL_STORAGE_KEYS.RECENT_PROJECTS, JSON.stringify([project?.item]));
    }
  }, [JSON.stringify(project)]);

  React.useEffect(() => {
    if (
      !isLoadingMyData &&
      (myData?.item?.projectUserProfile == null || (myData?.item?.projectUserProfile?.isLocked ?? false))
    ) {
      navigate(routes.common.noAccessRight.path, undefined, true, { replace: true });
    }
  }, [myData?.item?.projectUserProfile?.isLocked, myData?.item?.projectUserProfile == null]);

  return (
    <ApiErrorDisplay
      errors={[
        { error: errorMyData, genericError: 'Get profile failed' },
        { error: errorProject, genericError: 'Get project failed' },
      ]}
    />
  );
};
